import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from "@/components/ui/button";
import { 
  FileText, 
  Clock, 
  Trash2, 
  ChevronRight,
  Loader2,
  History
} from 'lucide-react';
import { toast } from 'sonner';
import './ScriptHistory.css';

const API = process.env.REACT_APP_BACKEND_URL;

function ScriptHistory({ onSelectScript, onClose }) {
  const [scripts, setScripts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const toggleSelect = (e, scriptId) => {
    e.stopPropagation();
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(scriptId)) next.delete(scriptId); else next.add(scriptId);
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelected((prev) => (prev.size === scripts.length ? new Set() : new Set(scripts.map(s => s.id))));
  };

  const handleBulkDelete = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Delete ${selected.size} script${selected.size > 1 ? 's' : ''}? This cannot be undone.`)) return;
    setBulkDeleting(true);
    try {
      const res = await axios.post(`${API}/api/scripts/bulk-delete`, { ids: [...selected] });
      setScripts(scripts.filter(s => !selected.has(s.id)));
      setSelected(new Set());
      toast.success(`${res.data.deleted} script${res.data.deleted !== 1 ? 's' : ''} deleted`);
    } catch (error) {
      console.error('Failed to delete scripts:', error);
      toast.error('Failed to delete scripts');
    } finally {
      setBulkDeleting(false);
    }
  };

  useEffect(() => {
    loadScripts();
  }, []);

  const loadScripts = async () => {
    try {
      const response = await axios.get(`${API}/api/scripts/list?limit=20`);
      setScripts(response.data);
    } catch (error) {
      console.error('Failed to load scripts:', error);
      toast.error('Failed to load script history');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectScript = async (scriptId) => {
    try {
      const response = await axios.get(`${API}/api/scripts/${scriptId}`);
      onSelectScript(response.data);
      toast.success('Script loaded');
    } catch (error) {
      console.error('Failed to load script:', error);
      toast.error('Failed to load script');
    }
  };

  const handleDelete = async (e, scriptId) => {
    e.stopPropagation();
    
    if (!window.confirm('Delete this script? This cannot be undone.')) {
      return;
    }

    setDeleting(scriptId);
    try {
      await axios.delete(`${API}/api/scripts/${scriptId}`);
      setScripts(scripts.filter(s => s.id !== scriptId));
      toast.success('Script deleted');
    } catch (error) {
      console.error('Failed to delete script:', error);
      toast.error('Failed to delete script');
    } finally {
      setDeleting(null);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffHours < 1) return 'Just now';
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
    });
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.round(seconds % 60);
    return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
  };

  if (loading) {
    return (
      <div className="script-history-panel">
        <div className="history-header">
          <h3><History size={18} /> Script History</h3>
          <button className="close-btn" onClick={onClose}>&times;</button>
        </div>
        <div className="history-loading">
          <Loader2 className="animate-spin" />
          <span>Loading scripts...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="script-history-panel">
      <div className="history-header">
        <h3><History size={18} /> Previous Scripts</h3>
        <button className="close-btn" onClick={onClose} data-testid="close-history">&times;</button>
      </div>

      {scripts.length === 0 ? (
        <div className="history-empty">
          <FileText className="empty-icon" />
          <p>No saved scripts yet</p>
          <span>Generated scripts will appear here</span>
        </div>
      ) : (
        <>
          <div className="history-select-bar">
            <label className="select-all-label">
              <input
                type="checkbox"
                checked={selected.size === scripts.length && scripts.length > 0}
                onChange={toggleSelectAll}
                data-testid="select-all-scripts"
              />
              Select all
            </label>
            {selected.size > 0 && (
              <button
                className="bulk-delete-btn"
                onClick={handleBulkDelete}
                disabled={bulkDeleting}
                data-testid="bulk-delete-scripts"
              >
                {bulkDeleting ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />}
                Delete ({selected.size})
              </button>
            )}
          </div>
          <div className="history-list">
          {scripts.map((script) => (
            <div 
              key={script.id} 
              className={`history-item ${selected.has(script.id) ? 'is-selected' : ''}`}
              onClick={() => handleSelectScript(script.id)}
              data-testid={`history-item-${script.id}`}
            >
              <input
                type="checkbox"
                className="item-check"
                checked={selected.has(script.id)}
                onChange={(e) => toggleSelect(e, script.id)}
                onClick={(e) => e.stopPropagation()}
                data-testid={`select-script-${script.id}`}
              />
              <div className="item-icon">
                <FileText size={18} />
              </div>
              <div className="item-content">
                <span className="item-title">{script.title}</span>
                <div className="item-meta">
                  <span className="item-date">
                    <Clock size={12} />
                    {formatDate(script.created_at)}
                  </span>
                  <span className="item-sections">
                    {script.section_count} sections • {formatDuration(script.total_duration)}
                  </span>
                </div>
              </div>
              <div className="item-actions">
                <button 
                  className="delete-btn"
                  onClick={(e) => handleDelete(e, script.id)}
                  disabled={deleting === script.id}
                  title="Delete"
                  data-testid={`delete-script-${script.id}`}
                >
                  {deleting === script.id ? (
                    <Loader2 size={14} className="animate-spin" />
                  ) : (
                    <Trash2 size={14} />
                  )}
                </button>
                <ChevronRight size={16} className="chevron" />
              </div>
            </div>
          ))}
          </div>
        </>
      )}
      
      <div className="history-footer">
        <p>Click a script to load it with all settings</p>
      </div>
    </div>
  );
}

export default ScriptHistory;
